// ======================================================================================================
// Private routes config
// - Define private routes, only accessible to authenticated users
// ======================================================================================================

import { Redirect } from 'react-router';

import { Routes } from './RoutesConfig';

import FourOFour from '@/ui/pages/404/404';
import { EmbeddableAnalyticsDashboardPage } from '@/ui/pages/analyticsDashboardPage/EmbeddableAnalyticsDashboardPage';
import AppleCallback from '@/ui/pages/appleCallbackPage/AppleCallbackPage';
import ApplianceEditorPage from '@/ui/pages/applianceEditorPage/ApplianceEditorPage';
import AutomationsPage from '@/ui/pages/automationsPage/AutomationsPage';
import AutomationsRebuildPage from '@/ui/pages/automationsRebuildPage/AutomationsRebuildPage';
import CreateEditAutomationPage from '@/ui/pages/automationsPageOld/CreateEditAutomationPage';
import ChangeProposalPage from '@/ui/pages/changeProposalPage/ChangeProposalPage';
import ClearCachePage from '@/ui/pages/ClearCachePage';
import CommissioningPage from '@/ui/pages/commissioningPage/CommissioningPage';
import CompanySettingsPage from '@/ui/pages/companySettingsPage/CompanySettingsPage';
import ConnectionApplication from '@/ui/pages/connectionApplicationPage/ConnectionApplication';
import { CreateEditDocumentTemplatePage } from '@/ui/pages/CreateEditDocumentTemplatePage';
import CreateEditEntityTemplatePage from '@/ui/pages/createEditEntityTemplatePage/CreateEditEntityTemplatePage';
import CsvUploadsPage from '@/ui/pages/csv-uploads-page/CsvUploadsPage';
import CustomerPage, {
  CustomerPageRedirect,
} from '@/ui/pages/customerPage/CustomerPage';
import DashboardNewPage from '@/ui/pages/dashboardNewPage/DashboardNewPage';
import DashboardPage from '@/ui/pages/dashboardPage/DashboardPage';
import DevPage from '@/ui/pages/devPage/DevPage';
import DocumentBlueprintPreviewPage from '@/ui/pages/documentBlueprintPreviewPage/DocumentBlueprintPreviewPage';
import DocumentOverviewPage from '@/ui/pages/documentOverviewPage/DocumentOverviewPage';
import DownloadApp from '@/ui/pages/downloadAppPage/DownloadAppPage';
import EditChangeProposalPage from '@/ui/pages/editChangeProposalPage/EditChangeProposalPage';
import EditInvoicePage from '@/ui/pages/editInvoicePage/EditInvoicePage';
import EditIsolatedPurchaseOrderPage from '@/ui/pages/editIsolatedPurchaseOrderPage/EditIsolatedPurchaseOrderPage';
import EditProtoInvoicePage from '@/ui/pages/editProtoInvoicePage/EditProtoInvoicePage';
import EnableCustomerPayments from '@/ui/pages/enableCustomerPaymentsPage/EnableCustomerPaymentsPage';
import EstimateBuilderPage from '@/ui/pages/estimateBuilderPage/EstimateBuilderPage';
import SolarDesignerPage from '@/ui/pages/solarDesignerPage/SolarDesignerPage';
import FormCreatePage from '@/ui/pages/formPage/FormCreatePage';
import FormPage from '@/ui/pages/formPage/FormPage';
import GrantAccess from '@/ui/pages/grantAccessPage/GrantAccessPage';
import InboxPage from '@/ui/pages/inboxPage/InboxPage';
import InvoicePage from '@/ui/pages/invoicePage/InvoicePage';
import { InvoicesPage } from '@/ui/pages/invoicesPage/InvoicesPage';
import LegacyInvoicePage from '@/ui/pages/legacyInvoicePage/LegacyInvoicePage';
import EditQuoteRedirectPage from '@/ui/pages/legacyQuoteRedirectPage/EditQuoteRedirectPage';
import NewQuoteRedirectPage from '@/ui/pages/legacyQuoteRedirectPage/NewQuoteRedirectPage';
import LegacyProposalPage from '@/ui/pages/legacyProposalPage/LegacyProposalPage';
import Customers from '@/ui/pages/listedCustomersPage/ListedCustomersPage';
import DealsPage from '@/ui/pages/listedDealsPage/ListedDealsPage';
import ItemGroups from '@/ui/pages/listedItemGroupsPage/ListedItemGroupsPage';
import Items from '@/ui/pages/listedItemsPage/ListedItemsPage';
import ListedMaterialsPage from '@/ui/pages/listedMaterialsPage/ListedMaterialsPage';
import ListedStockLocationsPage from '@/ui/pages/listedStockLocationsPage/ListedStockLocationsPage';
import ListedSuppliersPage from '@/ui/pages/listedSuppliersPage/ListedSuppliersPage';
import ListedTasksPage from '@/ui/pages/listedTasksPage/ListedTasksPage';
import Users from '@/ui/pages/listedUsersPage/ListedUsersPage';
import LoginOAuth from '@/ui/pages/loginOAuthPage/LoginOAuthPage';
import NewEditItemGroup from '@/ui/pages/newEditItemGroupPage/NewEditItemGroupPage';
import NewProposalPage from '@/ui/pages/newProposalPage/NewProposalPage';
import NewDiscountCodePage from '@/ui/pages/newServicePlanDiscountCode/NewDiscountCodePage';
import OAuth2Authorize from '@/ui/pages/oauth2AuthorizePage/OAuth2AuthorizePage';
import PersonalSettings from '@/ui/pages/personalSettingsPage/PersonalSettingsPage';
import PipelineConfigPage from '@/ui/pages/pipelineConfigPage/PipelineConfigPage';
import PipelinePage from '@/ui/pages/pipelinePage/PipelinePage';
import { PreviewDocumentInstancePage } from '@/ui/pages/PreviewDocumentInstancePage';
import ProjectPage, {
  ProjectPageRedirect,
} from '@/ui/pages/projectPage/ProjectPage';
import ProposalBuilderPage from '@/ui/pages/proposalBuilderPage/ProposalBuilderPage';
import PurchaseOrderPage from '@/ui/pages/purchaseOrderPage/PurchaseOrderPage';
import ResetPassword from '@/ui/pages/resetPasswordPage/ResetPasswordPage';
import ScheduleDispatchPage from '@/ui/pages/scheduleDispatchPage/ScheduleDispatchPage';
import ServicePlanPage from '@/ui/pages/servicePlanPage/ServicePlanPage';
import ServicePlanPeriodPage from '@/ui/pages/servicePlanPeriodPage/ServicePlanPeriodPage';
import ServicePlansCustomerReminders from '@/ui/pages/servicePlansCustomerReminders/ServicePlansCustomerReminders';
import ServicePlansPage from '@/ui/pages/servicePlansPage/ServicePlansPage';
import SetupBusinessPage from '@/ui/pages/setupBusinessPage/SetupBusinessPage';
import SetupUserPage from '@/ui/pages/setupUserPage/SetupUserPage';
import StockLocationPage from '@/ui/pages/stockLocationPage/StockLocationPage';
import SupplierPage from '@/ui/pages/supplierPage/SupplierPage';
import TimelogReportingPage from '@/ui/pages/timelogReportingPage/TimelogReportingPage';
import TrialPeriodExpiryHoldingPage from '@/ui/pages/trialPeriodExpiryHoldingPage/TrialPeriodExpiryHoldingPage';
import UserInvite from '@/ui/pages/userInvitePage/UserInvitePage';
import UserPage from '@/ui/pages/userPage/UserPage';
import VerifyUser from '@/ui/pages/verifyUserPage/VerifyUserPage';
import XeroCallback from '@/ui/pages/xeroCallbackPage/XeroCallbackPage';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { AnalyticsPermissions } from '@payaca/permissions/analytics/analytics.permissons';
import { AppliancesPermissions } from '@payaca/permissions/appliances/appliances.permissions';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';
import { CustomersPermissions } from '@payaca/permissions/customers/customers.permissions';
import { DashboardPermissions } from '@payaca/permissions/dashboard/dashboard.permissions';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { DocumentInstancePermissions } from '@payaca/permissions/documentInstance/documentInstance.permissions';
import { DocumentsPermissions } from '@payaca/permissions/documents/documents.permissions';
import { DocumentTemplatePermissions } from '@payaca/permissions/documentTemplate/documentTemplate.permissions';
import { EstimatesPermissions } from '@payaca/permissions/estimates/estimates.permissions';
import { InboundEmailsPermissions } from '@payaca/permissions/inbound-emails/inbound-emails.permissions';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { LineItemsPermissions } from '@payaca/permissions/lineItems/line-items.permissions';
import { MaterialsPermissions } from '@payaca/permissions/materials/materials.permissions';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { PipelinesPermissions } from '@payaca/permissions/pipelines/pipelines.permissions';
import { ProposalsPermissions } from '@payaca/permissions/proposals/proposals.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { ServicePlanPermissions } from '@payaca/permissions/service-plans/service-plans.permissions';
import { StockLocationsPermissions } from '@payaca/permissions/stockLocations/stockLocations.permissions';
import { SuppliersPermissions } from '@payaca/permissions/suppliers/suppliers.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import ChildAccountPage from '../ui/pages/childAccountPage/ChildAccountPage';
import ListedChildAccountsPage from '../ui/pages/listedChildAccountsPage/ListedChildAccountsPage';
import ManageSubscription from '../ui/pages/manageSubscriptionPage/ManageSubscriptionPage';

/** Old /templates/:tab URLs mapped to each template type's settings home. */
const LEGACY_TEMPLATE_TAB_ROUTES = {
  'proposal-templates': '/companySettings/sales-payments/proposal-templates',
  'scheduledevent-templates': '/companySettings/operations/event-templates',
  'task-templates': '/companySettings/operations/task-templates',
  'document-templates': '/companySettings/custom-data/document-templates',
};

/**
 * A route entry that redirects an old URL to its new home, carrying the query
 * string through. `to` maps the matched params to the target path.
 */
const legacyRedirect = (id, path, to) => ({
  id,
  path,
  renderComponent: (props) => (
    <Redirect to={`${to(props.match.params)}${props.location.search}`} />
  ),
});

const devRoutes = [{ id: Routes.DEV, path: 'dev', component: DevPage }];

const setupUserRoutes = [
  { id: Routes.SETUP_USER, path: 'setupUser', component: SetupUserPage },
];

const requiresSetupAccountRoutes = [
  {
    id: Routes.SETUP_BUSINESS_PAGE,
    path: 'setupBusiness',
    component: SetupBusinessPage,
  },
];

const adminTrialPeriodExpiredRoutes = [
  {
    id: Routes.UPGRADE_ACCOUNT,
    path: 'upgradeAccount',
    component: ManageSubscription,
  },
];

const trialPeriodExpiredRoutes = [
  {
    id: Routes.UPGRADE_ACCOUNT,
    path: 'trialExpired',
    component: TrialPeriodExpiryHoldingPage,
  },
];

const adminUserSeatsExpiredRoutes = [
  {
    id: Routes.UPGRADE_ACCOUNT,
    path: 'upgradeAccount',
    component: ManageSubscription,
  },
  { id: Routes.USERS, path: 'companySettings/team/users', component: Users },
  legacyRedirect(
    Routes.LEGACY_USERS,
    'users',
    () => '/companySettings/team/users'
  ),
  {
    id: Routes.PERSONAL_SETTINGS,
    path: 'personalSettings',
    component: PersonalSettings,
  },
];

const userSeatsExceededRoutes = [
  {
    id: Routes.PERSONAL_SETTINGS,
    path: 'personalSettings',
    component: PersonalSettings,
  },
];

const getParentAccountAdminRoutes = [
  {
    id: Routes.LISTED_CHILD_ACCOUNTS,
    path: 'accounts',
    component: ListedChildAccountsPage,
    requiresPermission: [AccountsPermissions.VIEW_CHILD_ACCOUNTS],
  },
  {
    id: Routes.CHILD_ACCOUNT,
    path: 'accounts/:accountId',
    component: ChildAccountPage,
    exact: false,
    requiresPermission: [AccountsPermissions.VIEW_CHILD_ACCOUNTS],
  },
  {
    id: Routes.USERS,
    path: 'users',
    component: Users,
    requiresPermission: [AccountsPermissions.GET_DETAILED_USERS],
  },
  {
    id: Routes.USER,
    path: 'users/:userId',
    component: (props) => <UserPage userId={+props.match.params.userId} />,
    requiresPermission: [AccountsPermissions.GET_DETAILED_USERS],
    exact: false,
  },
];

const getStandardRoutes = () => [
  {
    id: Routes.APPLE_CALLBACK,
    path: 'apple/callback',
    component: AppleCallback,
  },
  {
    id: Routes.XERO_CALLBACK,
    path: 'xero/callback',
    component: XeroCallback,
  },
  {
    id: Routes.VERIFY_USER,
    path: 'verify/:token',
    renderComponent: (props) => <VerifyUser {...props} isAuthed={true} />,
  },
  {
    id: Routes.ITEM_GROUPS,
    path: 'items/groups',
    component: ItemGroups,
    requiresPermission: [LineItemsPermissions.GET_LINE_ITEM_GROUPS],
  },
  {
    id: Routes.EDIT_ITEM_GROUP,
    path: 'items/groups/:lineItemGroupId',
    component: NewEditItemGroup,
    requiresPermission: [LineItemsPermissions.UPDATE_LINE_ITEM_GROUP],
  },
  {
    id: Routes.ITEMS,
    path: 'items',
    component: Items,
    exact: false,
    requiresPermission: [LineItemsPermissions.GET_COLLECTION_MANAGEMENT_VIEW],
  },
  {
    id: Routes.INBOX,
    path: 'inbox/:emailId?',
    exact: false,
    component: InboxPage,
    requiresPermission: [InboundEmailsPermissions.GET_INBOUND_EMAILS],
  },
  {
    id: Routes.INVOICES,
    path: 'invoices',
    component: InvoicesPage,
    requiresPermission: [InvoicesPermissions.GET_LISTED_INVOICES],
  },
  // Kept for old bookmarks and saved links — GA still shows direct
  // landings on the pre-settings-IA URLs (also tags, templates, blueprints).
  legacyRedirect(
    Routes.LEGACY_USERS,
    'users',
    () => '/companySettings/team/users'
  ),
  {
    id: Routes.USER,
    path: 'users/:userId',
    component: (props) => <UserPage userId={+props.match.params.userId} />,
    requiresPermission: [AccountsPermissions.GET_DETAILED_USERS],
    exact: false,
  },
  legacyRedirect(
    Routes.LEGACY_TAGS,
    'tags',
    () => '/companySettings/custom-data/tags'
  ),
  {
    id: Routes.NEW_QUOTE,
    path: 'quotes/new',
    component: NewQuoteRedirectPage,
    requiresPermission: [JobPermissions.ADD_JOB],
  },
  {
    id: Routes.EDIT_QUOTE,
    path: 'quotes/:quoteId',
    component: (props) => (
      <EditQuoteRedirectPage proposalId={props.match.params.quoteId} />
    ),
    requiresPermission: [JobPermissions.UPDATE_JOB],
    exact: false,
  },
  {
    id: Routes.INVOICE,
    path: 'projects/:projectId/invoices/:invoiceId',
    component: (props) => {
      return (
        <InvoicePage
          invoiceId={+props.match.params.invoiceId}
          projectId={+props.match.params.projectId} // TODO: should use project publicId will need updating in all internal links
        />
      );
    },
    requiresPermission: [
      InvoicesPermissions.GET_INVOICES,
      InvoicesPermissions.GET_ASSIGNED_INVOICES,
    ],
  },
  {
    id: Routes.EDIT_PROTO_INVOICE,
    path: 'projects/:projectId/invoices/quick/:invoiceId/edit',
    component: (props) => {
      return (
        <EditProtoInvoicePage
          protoInvoiceId={+props.match.params.invoiceId}
          projectId={+props.match.params.projectId} // TODO: should use project publicId will need updating in all internal links
        />
      );
    },
    exact: false,
    requiresPermission: [
      InvoicesPermissions.GET_INVOICES,
      InvoicesPermissions.GET_ASSIGNED_INVOICES,
      JobPermissions.UPDATE_JOB,
      JobPermissions.GET_JOB,
    ],
  },
  {
    id: Routes.EDIT_INVOICE,
    path: 'projects/:projectId/invoices/:invoiceId/edit/:view?',
    component: (props) => {
      return (
        <EditInvoicePage
          invoiceId={+props.match.params.invoiceId}
          projectId={+props.match.params.projectId} // TODO: should use project publicId will need updating in all internal links
          viewType={props.match.params.view}
        />
      );
    },
    requiresPermission: [
      InvoicesPermissions.GET_INVOICES,
      InvoicesPermissions.GET_ASSIGNED_INVOICES,
    ],
  },

  {
    id: Routes.LISTED_TASKS,
    path: 'tasks',
    component: ListedTasksPage,
    requiresPermission: [
      TasksPermissions.GET_TASKS,
      TasksPermissions.GET_SELF_ASSIGNED_TASKS,
    ],
    exact: false,
  },
  {
    id: Routes.LISTED_SUPPLIERS,
    path: 'suppliers',
    component: ListedSuppliersPage,
    requiresPermission: [SuppliersPermissions.GET_COLLECTION_MANAGEMENT_VIEW],
  },
  {
    id: Routes.SUPPLIER,
    path: 'suppliers/:supplierId',
    component: (props) => {
      return <SupplierPage supplierId={+props.match.params.supplierId} />;
    },
    requiresPermission: [SuppliersPermissions.GET_SUPPLIERS],
    exact: false,
  },
  {
    id: Routes.LISTED_STOCK_LOCATIONS,
    path: 'stock-locations',
    component: ListedStockLocationsPage,
    requiresPermission: [StockLocationsPermissions.GET_STOCK_LOCATIONS],
  },
  {
    id: Routes.STOCK_LOCATION,
    path: 'stock-locations/:stockLocationId',
    component: (props) => {
      return (
        <StockLocationPage
          stockLocationId={props.match.params.stockLocationId}
        />
      );
    },
    requiresPermission: [StockLocationsPermissions.GET_STOCK_LOCATIONS],
    exact: false,
  },
  {
    id: Routes.LISTED_MATERIALS,
    path: 'materials',
    exact: false,
    component: ListedMaterialsPage,
    requiresPermission: [MaterialsPermissions.GET_COLLECTION_MANAGEMENT_VIEW],
  },
  {
    id: Routes.CREATE_EDIT_ENTITY_TEMPLATE,
    path: 'templates/edit/:templatePublicId?',
    renderComponent: (props) => {
      return (
        <CreateEditEntityTemplatePage
          templatePublicId={props.match.params.templatePublicId}
        />
      );
    },
    requiresPermission: [TemplatesPermissions.PERSIST_TEMPLATE],
  },
  {
    id: Routes.CREATE_EDIT_DOCUMENT_TEMPLATE,
    path: 'templates/document-templates/edit/:templateId',
    renderComponent: (props) => {
      return (
        <CreateEditDocumentTemplatePage
          templateId={props.match.params.templateId}
        />
      );
    },
    requiresPermission: [DocumentTemplatePermissions.CREATE_DOCUMENT_TEMPLATE],
  },
  {
    id: Routes.CREATE_DOCUMENT_TEMPLATE,
    path: 'templates/document-templates/new',
    renderComponent: () => {
      return <CreateEditDocumentTemplatePage />;
    },
    requiresPermission: [DocumentTemplatePermissions.CREATE_DOCUMENT_TEMPLATE],
  },
  {
    id: Routes.CREATE_DOCUMENT_TEMPLATE_MANUAL,
    path: 'templates/document-templates/new/manual',
    renderComponent: () => {
      return <CreateEditDocumentTemplatePage createVariant="manual" />;
    },
    requiresPermission: [DocumentTemplatePermissions.CREATE_DOCUMENT_TEMPLATE],
  },
  {
    id: Routes.PREVIEW_DOCUMENT_INSTANCE,
    path: 'projects/:projectId/documents/preview/:templateId',
    renderComponent: (props) => {
      return (
        <PreviewDocumentInstancePage
          templateId={props.match.params.templateId}
          projectId={props.match.params.projectId}
        />
      );
    },
    requiresPermission: [DocumentInstancePermissions.CREATE_DOCUMENT_INSTANCE],
  },
  // Guard with hasOwn: the segment comes straight from the URL, and a bare
  // object lookup would resolve inherited keys like "constructor".
  legacyRedirect(Routes.LEGACY_TEMPLATES, 'templates/:tab?', ({ tab }) =>
    tab && Object.hasOwn(LEGACY_TEMPLATE_TAB_ROUTES, tab)
      ? LEGACY_TEMPLATE_TAB_ROUTES[tab]
      : '/companySettings/operations/event-templates'
  ),
  {
    id: Routes.DOCUMENT_BLUEPRINT_PREVIEW,
    path: 'companySettings/custom-data/ready-made-library/:blueprintId',
    component: DocumentBlueprintPreviewPage,
    requiresPermission: [DocumentTemplatePermissions.CREATE_DOCUMENT_TEMPLATE],
  },
  legacyRedirect(
    Routes.LEGACY_DOCUMENT_BLUEPRINTS,
    'document-blueprints/:blueprintId?',
    ({ blueprintId }) =>
      `/companySettings/custom-data/ready-made-library${
        blueprintId ? `/${blueprintId}` : ''
      }`
  ),
  {
    id: Routes.TIMELOG_REPORTING,
    path: 'timelogs',
    component: TimelogReportingPage,
    requiresPermission: [TimelogsPermissions.PREVIEW_TIMELOGS],
  },
  // this is an edit page
  {
    id: Routes.EDIT_ISOLATED_PURCHASE_ORDER,
    path: 'purchaseorders/:purchaseOrderId/edit',
    component: (props) => {
      return (
        <EditIsolatedPurchaseOrderPage
          purchaseOrderId={+props.match.params.purchaseOrderId}
        />
      );
    },
    requiresPermission: [
      MaterialsListPermissions.PERSIST_ISOLATED_PURCHASE_ORDER,
    ],
  },
  // this is a preview page
  {
    id: Routes.VIEW_PURCHASE_ORDER,
    path: 'purchaseorders/:purchaseOrderId',
    component: (props) => {
      return (
        <PurchaseOrderPage
          purchaseOrderId={+props.match.params.purchaseOrderId}
        />
      );
    },
    requiresPermission: [MaterialsListPermissions.GET_PURCHASE_ORDERS],
  },
  {
    id: Routes.EDIT_CHANGE_PROPOSAL,
    path: 'projects/:projectId/changeproposals/:changeProposalId/edit',
    component: (props) => {
      return (
        <EditChangeProposalPage
          changeProposalId={+props.match.params.changeProposalId}
          projectId={props.match.params.projectId}
        />
      );
    },
    requiresPermission: [ProposalsPermissions.PERSIST_CHANGE_PROPOSAL],
    exact: false,
  },
  {
    id: Routes.ESTIMATE_BUILDER,
    path: 'projects/:projectId/estimates/:estimateId',
    component: EstimateBuilderPage,
    requiresPermission: [EstimatesPermissions.UPDATE_ESTIMATE],
  },
  {
    id: Routes.SOLAR_DESIGNER,
    path: 'projects/:projectId/solar-designer',
    component: SolarDesignerPage,
    requiresPermission: [EstimatesPermissions.UPDATE_ESTIMATE],
  },
  {
    id: Routes.NEW_PROPOSAL_V2,
    path: 'projects/:projectId/proposals/new',
    component: NewProposalPage,
    requiresPermission: [JobPermissions.ADD_JOB],
    exact: true,
  },
  {
    id: Routes.PROPOSAL_BUILDER_V2,
    path: 'projects/:projectId/proposals/:proposalId',
    component: ProposalBuilderPage,
    requiresPermission: [JobPermissions.UPDATE_JOB],
    exact: false,
  },
  {
    id: Routes.CHANGE_PROPOSAL,
    path: 'projects/:projectId/changeproposals/:changeProposalId',
    component: (props) => {
      return (
        <ChangeProposalPage
          changeProposalId={+props.match.params.changeProposalId}
          projectId={props.match.params.projectId}
        />
      );
    },
    requiresPermission: [ProposalsPermissions.GET_CHANGE_PROPOSALS],
  },
  // Non-exact so every legacy invoice URL beneath it lands on the same page.
  {
    id: Routes.LEGACY_INVOICE,
    path: 'invoices/:invoiceId',
    exact: false,
    component: LegacyInvoicePage,
  },
  // Non-exact to match the legacy invoice page.
  {
    id: Routes.LEGACY_PROPOSAL,
    path: 'proposals/:proposalId',
    exact: false,
    component: LegacyProposalPage,
  },
  {
    id: Routes.CONNECTION_APPLICATION,
    path: 'projects/:projectId/compliance/connection-application/:connectionApplicationId',
    component: ConnectionApplication,
    requiresPermission: [DealsPermissions.GET_COMPLIANCE_FORMS],
  },
  {
    id: Routes.CONNECTION_APPLICATION_COMMISSIONING,
    path: 'projects/:projectId/compliance/connection-application/:connectionApplicationId/commissioning',
    component: CommissioningPage,
    requiresPermission: [DealsPermissions.GET_COMPLIANCE_FORMS],
  },
  {
    id: Routes.CUSTOMERS,
    path: 'customers',
    component: Customers,
    requiresPermission: [CustomersPermissions.GET_COLLECTION_MANAGEMENT_VIEW],
  },
  {
    id: Routes.NEW_CUSTOMER,
    path: 'customers/new',
    component: () => <Customers isCreatingCustomer />,
    requiresPermission: [CustomersPermissions.ADD_CUSTOMER],
  },
  {
    id: Routes.CUSTOMER,
    path: 'customers/:customerId',
    component: (props) => {
      const customerId = props.match.params.customerId;
      if (!isNaN(Number(customerId))) {
        // legacy customerId - redirect to publicId
        return <CustomerPageRedirect {...props} />;
      }
      return <CustomerPage {...props} />;
    },
    requiresPermission: [CustomersPermissions.GET_CUSTOMERS],
    exact: false,
  },
  {
    id: Routes.PERSONAL_SETTINGS,
    path: 'personalSettings/:tab?',
    component: PersonalSettings,
  },
  {
    id: Routes.ADD_APPLIANCE,
    path: 'companySettings/appliances/new/:category',
    component: ApplianceEditorPage,
    requiresPermission: [AppliancesPermissions.MANAGE_APPLIANCES],
  },
  {
    id: Routes.EDIT_APPLIANCE,
    path: 'companySettings/appliances/edit/:applianceId',
    component: ApplianceEditorPage,
    requiresPermission: [AppliancesPermissions.MANAGE_APPLIANCES],
  },
  // The old URL is a live OAuth return target: the Xero/Quickbooks
  // redirect_uri is built as `${host}/connections/<service>` here and in
  // server-backend, and is registered with those providers.
  legacyRedirect(
    Routes.LEGACY_CONNECTIONS,
    'connections/:service?',
    ({ service }) =>
      `/companySettings/integrations-data/connections${
        service ? `/${service}` : ''
      }`
  ),
  {
    id: Routes.COMPANY_SETTINGS,
    // `detail` is a section's trailing segment — today only the Connections
    // OAuth return service (connections/xero). Sections like Users or Tags
    // carry their own permission gates, so the route accepts any of the
    // roles below; per-section access is enforced by the permission-gated
    // nav data (an invisible section redirects away in CompanySettingsPage).
    path: 'companySettings/:category?/:section?/:detail?',
    exact: false,
    component: CompanySettingsPage,
    requiresPermission: [
      AccountsPermissions.INVITE_USER,
      AccountsPermissions.GET_DETAILED_USERS,
      AccountsPermissions.MANAGE_TAGS,
      TemplatesPermissions.GET_TEMPLATES,
      AccountsPermissions.ADD_CONNECTION,
      AccountsPermissions.ADD_CONNECTION_AUTOMATION,
      AccountsPermissions.ADD_CONNECTION_ACCOUNTING,
      AccountsPermissions.ADD_CONNECTION_PAYMENT,
      AccountsPermissions.ADD_CONNECTION_SUPPLIERS,
    ],
  },
  {
    id: Routes.UPGRADE_ACCOUNT,
    path: 'upgradeAccount',
    component: ManageSubscription,
    requiresPermission: [AccountsPermissions.UPDATE_SUBSCRIPTION],
  },
  {
    id: Routes.EMBEDDABLE_ANALYTICS_DASHBOARD,
    path: 'analytics/embed/:identifier',
    component: EmbeddableAnalyticsDashboardPage,
    requiresPermission: [AnalyticsPermissions.GET_CHART_DATA],
  },
  {
    id: Routes.DASHBOARD,
    path: 'dashboard',
    component: DashboardPage,
    requiresPermission: [
      DashboardPermissions.GET_ACTION_REQUIRED_PAYMENT_OUTSTANDING_INVOICES,
      DashboardPermissions.GET_ACTION_REQUIRED_PENDING_PROPOSALS,
      DashboardPermissions.GET_ACTION_REQUIRED_DRAFT_JOBS,
      DashboardPermissions.GET_PENDING_PAYMENTS,
    ],
  },
  {
    id: Routes.DASHBOARD_NEW,
    path: 'dashboard-new',
    component: DashboardNewPage,
    requiresPermission: [
      DashboardPermissions.GET_ACTION_REQUIRED_PAYMENT_OUTSTANDING_INVOICES,
      DashboardPermissions.GET_ACTION_REQUIRED_PENDING_PROPOSALS,
      DashboardPermissions.GET_ACTION_REQUIRED_DRAFT_JOBS,
      DashboardPermissions.GET_PENDING_PAYMENTS,
    ],
  },

  {
    id: Routes.ENABLE_CUSTOMER_PAYMENTS,
    path: 'enableCustomerPayments',
    component: EnableCustomerPayments,
  },
  {
    id: Routes.RESET,
    path: 'login/reset/:token',
    renderComponent: (props) => <ResetPassword {...props} isAuthed={true} />,
  },
  {
    id: Routes.USER_INVITE,
    path: 'invite/:token',
    renderComponent: (props) => <UserInvite {...props} isAuthed={true} />,
    requiresPermission: [AccountsPermissions.INVITE_USER],
  },
  {
    id: Routes.OAUTH2_AUTHORIZE,
    path: 'oauth2/authorize',
    renderComponent: (props) => <OAuth2Authorize {...props} />,
  },
  {
    id: Routes.LOGIN_OAUTH,
    path: 'oauth',
    renderComponent: (props) => <LoginOAuth {...props} isAuthed={true} />,
  },
  { id: Routes.GRANT_ACCESS, path: 'grantAccess', component: GrantAccess },
  {
    id: Routes.DEALS,
    path: 'deals',
    component: DealsPage,
    requiresPermission: [
      DealsPermissions.GET_LISTED_DEALS,
      DealsPermissions.GET_MY_LISTED_DEALS,
    ],
  },
  {
    id: Routes.PIPELINE,
    path: 'pipelines/:pipelineId',
    component: PipelinePage,
    requiresPermission: [
      DealsPermissions.GET_LISTED_DEALS,
      DealsPermissions.GET_MY_LISTED_DEALS,
    ],
  },
  {
    id: Routes.PIPELINE_CONFIG,
    path: 'pipelines/:pipelineId/config',
    component: PipelineConfigPage,
    requiresPermission: [PipelinesPermissions.EDIT_PIPELINE],
  },
  {
    id: Routes.ARCHIVED_DEALS,
    path: 'deals/archived',
    renderComponent: (props) => (
      <DealsPage {...props} showArchivedDeals={true} />
    ),
    requiresPermission: [
      DealsPermissions.GET_LISTED_DEALS,
      DealsPermissions.GET_MY_LISTED_DEALS,
    ],
  },
  {
    path: 'deals/:dealId',
    component: ProjectPageRedirect,
    requiresPermission: [
      DealsPermissions.GET_DEAL,
      DealsPermissions.GET_MY_DEAL,
    ],
    exact: false,
  },
  {
    id: Routes.PROJECT_PAGE,
    path: 'projects/:projectId',
    component: (props) => {
      if (!isNaN(Number(props.match.params.projectId))) {
        return <ProjectPageRedirect {...props} />;
      }
      return <ProjectPage projectId={props.match.params.projectId} />;
    },
    requiresPermission: [
      DealsPermissions.GET_DEAL,
      DealsPermissions.GET_MY_DEAL,
    ],
    exact: false,
  },
  {
    id: Routes.CREATE_FORM,
    path: 'forms',
    component: FormCreatePage,
    requiresPermission: [DocumentsPermissions.ADD_DOCUMENT],
  },
  {
    id: Routes.NEW_EDIT_FORM,
    path: 'forms/:formPreviewToken/:pageIndex',
    component: FormPage,
  },
  {
    id: Routes.DOCUMENT_OVERVIEW_PAGE,
    path: 'documents/:documentId',
    component: DocumentOverviewPage,
    requiresPermission: [DocumentsPermissions.GET_DOCUMENTS_FOR_DEAL],
  },
  {
    id: Routes.SCHEDULE_DISPATCH,
    path: 'schedule',
    component: ScheduleDispatchPage,
    requiresPermission: [
      ScheduledEventsPermissions.GET_EVENTS,
      ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
      ScheduledEventsPermissions.GET_MY_EVENTS,
    ],
    exact: false,
  },

  {
    id: Routes.AUTOMATIONS_PAGE,
    path: 'automations',
    component: AutomationsPage,
    requiresPermission: [AutomationsPermissions.GET_LISTED_AUTOMATIONS],
  },
  ...(import.meta.env.VITE_ENABLE_AUTOMATIONS_REBUILD === 'true'
    ? [
        {
          id: Routes.AUTOMATIONS_REBUILD_PAGE,
          path: 'automations-rebuild',
          component: AutomationsRebuildPage,
          requiresPermission: [AutomationsPermissions.GET_LISTED_AUTOMATIONS],
        },
      ]
    : []),
  {
    id: Routes.CREATE_EDIT_AUTOMATION_PAGE,
    path: 'automations/:automationId',
    component: CreateEditAutomationPage,
    requiresPermission: [
      AutomationsPermissions.ADD_AUTOMATION,
      AutomationsPermissions.EDIT_AUTOMATION,
    ],
  },
  {
    id: Routes.CSV_UPLOADS,
    path: 'csv-uploads',
    component: CsvUploadsPage,
    requiresPermission: [
      CustomersPermissions.BULK_IMPORT,
      LineItemsPermissions.BULK_IMPORT,
    ],
  },
  {
    id: Routes.SERVICE_PLAN_PERIOD,
    path: 'service-plans/service-period/:servicePlanPeriodId',
    component: (props) => {
      return (
        <ServicePlanPeriodPage
          servicePlanPeriodId={props.match.params.servicePlanPeriodId}
        />
      );
    },
    requiresPermission: [ServicePlanPermissions.GET_SERVICE_PLANS],
  },
  {
    id: Routes.SERVICE_PLANS,
    path: 'service-plans/:tab?',
    component: ServicePlansPage,
    requiresPermission: [ServicePlanPermissions.GET_SERVICE_PLANS],
  },

  {
    id: Routes.SERVICE_PLAN,
    path: 'service-plans/service-plan/new',
    component: ServicePlanPage,
    requiresPermission: [ServicePlanPermissions.PERSIST_SERVICE_PLAN],
  },
  {
    id: Routes.SERVICE_PLAN,
    path: 'service-plans/service-plan/:servicePlanId',
    component: ServicePlanPage,
    requiresPermission: [ServicePlanPermissions.GET_SERVICE_PLANS],
  },
  {
    id: Routes.DISCOUNT_CODES,
    path: 'service-plans/discount-codes/new',
    component: NewDiscountCodePage,
    requiresPermission: [ServicePlanPermissions.PERSIST_DISCOUNT],
  },
  {
    id: Routes.CUSTOMER_REMINDERS,
    path: 'service-plans/customer-reminders/:customerReminderId',
    component: ServicePlansCustomerReminders,
    requiresPermission: [ServicePlanPermissions.PERSIST_SERVICE_PLAN],
  },
];

const sharedRouteConfig = [
  {
    id: Routes.CLEAR_CACHE,
    path: '_clear-cache',
    component: ClearCachePage,
  },
];

const getUsersPrivateRoutes = ({
  userHasRequiredPermission,
  canUpdateSubscription,
  userHasContactNumber,
  accountIsSetup,
  trialPeriodIsExpired,
  hasExceededUserSeats,
  hasBetaFeatures,
  showParentAccountAdminRoutes,
}) => {
  let routeConfig;
  switch (true) {
    case !userHasContactNumber:
      routeConfig = setupUserRoutes;
      break;
    case !accountIsSetup:
      routeConfig = requiresSetupAccountRoutes;
      break;
    case trialPeriodIsExpired:
      routeConfig = canUpdateSubscription
        ? adminTrialPeriodExpiredRoutes
        : trialPeriodExpiredRoutes;
      break;
    case hasExceededUserSeats:
      routeConfig = canUpdateSubscription
        ? adminUserSeatsExpiredRoutes
        : userSeatsExceededRoutes;
      break;
    case showParentAccountAdminRoutes:
      routeConfig = getParentAccountAdminRoutes;
      break;
    default:
      routeConfig = getStandardRoutes({
        hasBetaFeatures,
      });
      break;
  }
  routeConfig = [...routeConfig, ...sharedRouteConfig];
  return routeConfig
    .filter((route) => {
      // requires a permission to be visible?
      if (route.requiresPermission)
        return userHasRequiredPermission(route.requiresPermission);
      // no conditions applied - include
      return true;
    })
    .reduce((acc, route) => {
      acc[route.id] = route;
      return acc;
    }, {});
};

const commonRedirectRoutes = [
  '',
  'login',
  'register',
  'verifyHolding',
  'quotes',
  'invoices',
  'nonAdminMobileRedirect',
  'setupUser',
];

const getUsersPrivateRedirects = ({
  userHasRequiredPermission,
  canUpdateSubscription,
  isMobile,
  accountIsSetup,
  hasExceededUserSeats,
  trialPeriodIsExpired,
  userHasContactNumber,
  isFormsRoute,
  showParentAccountAdminRoutes,
}) => {
  switch (true) {
    case isMobile && !isFormsRoute:
      return {
        TO_DOWNLOAD_APP: {
          fromPaths: ['*'],
          toPath: '/',
          component: DownloadApp,
        },
      };
    case !userHasContactNumber:
      return {
        TO_SETUP_USER: {
          fromPaths: ['*'],
          toPath: 'setupUser',
        },
      };
    case !accountIsSetup:
      return {
        TO_SETUP_ACCOUNT_PAGE: {
          fromPaths: ['*'],
          toPath: `setupBusiness${window.location.search}`,
        },
      };
    case trialPeriodIsExpired:
      return canUpdateSubscription
        ? {
            TO_UPGRADE_ACCOUNT: {
              fromPaths: ['*'],
              toPath: 'upgradeAccount',
            },
          }
        : {
            TO_TRIAL_EXPIRED: {
              fromPaths: ['*'],
              toPath: 'trialExpired',
            },
          };
    case hasExceededUserSeats:
      return canUpdateSubscription
        ? {
            TO_UPGRADE_ACCOUNT: {
              fromPaths: ['*'],
              toPath: 'upgradeAccount',
            },
          }
        : {
            TO_TRIAL_EXPIRED: {
              fromPaths: ['*'],
              toPath: 'resources',
            },
          };
    case showParentAccountAdminRoutes: {
      const standardRoutePaths = getStandardRoutes().map((route) => route.path);
      return {
        TO_CHILD_ACCOUNTS: {
          fromPaths: [
            ...commonRedirectRoutes,
            ...standardRoutePaths,
            ...standardRoutePaths.map((x) => `${x}/*`),
          ],
          toPath: 'accounts',
        },
        TO_404: {
          fromPaths: ['*'],
          toPath: '404',
          component: FourOFour,
        },
      };
    }
    default: {
      const canViewDashboard = userHasRequiredPermission([
        DashboardPermissions.GET_ACTION_REQUIRED_PAYMENT_OUTSTANDING_INVOICES,
        DashboardPermissions.GET_ACTION_REQUIRED_PENDING_PROPOSALS,
        DashboardPermissions.GET_ACTION_REQUIRED_DRAFT_JOBS,
        DashboardPermissions.GET_PENDING_PAYMENTS,
      ]);

      const canViewDeals = userHasRequiredPermission([
        DealsPermissions.GET_LISTED_DEALS,
        DealsPermissions.GET_MY_LISTED_DEALS,
      ]);

      let rootToPath = 'schedule';
      if (canViewDashboard) {
        rootToPath = 'dashboard';
      } else if (canViewDeals) {
        rootToPath = 'deals';
      }

      return {
        TO_ROOT_PAGE: {
          fromPaths: [...commonRedirectRoutes, 'accounts', 'accounts/*'],
          toPath: rootToPath,
        },
        TO_404: {
          fromPaths: ['*'],
          toPath: '404',
          component: FourOFour,
        },
      };
    }
  }
};

export { getUsersPrivateRedirects, getUsersPrivateRoutes };
